import { formatDate } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  Clinic,
  Coverage,
  FollowUpSelector,
  GestationNutritionRecommendation,
  NutritionRecommendation,
  PetInfo,
  Product,
  ProductRecommendationBack,
  ProductRecommendationV2,
  ProductV2,
  RationingApiBody,
  SmartRecoApiBody,
} from '@app/core/models';
import { Market } from '@app/core/models/market';
import { RecommendationProgramApiBody } from '@app/core/models/program';
import { Constants, Helper, LifestageType, MeasureHelper, MeasurementCodeType, Programs } from '@app/shared/utils';
import { formatNutritionRecommendation } from '@app/shared/utils/static-helpers/rationing-helper';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BreedPetProfileModel, BreedPetProfileParameters, defaultLifestagesPlanDuration } from '../models/BreedController';
import { ApiService } from './../api.service';
import { environment } from '@env/environment';
import { formatNutritionalAssessment, formatPetInfoToNextVisit } from '@app/core/services/utils/nutrition-helper';
import { NextVisitData, NextVisitFromBack } from '@app/core/models/visit-next';
import { ProductMapperService } from '../product/product-mapper.service';
import { ServicesHelper } from '../../utils';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';

@Injectable({ providedIn: 'root' })
export class NutritionService extends ApiService {
  constructor(
    private http: HttpClient,
    @Inject(LOCALE_ID) protected localeId: string,
    private readonly productMapperService: ProductMapperService,
  ) {
    super();
  }

  createRecommendation(
    program: Programs,
    body: RationingApiBody,
    productIds: string[],
    productsEan: string[],
    productsMainItemId: number[],
    withCups: boolean,
    country: string,
    withAnalytics = true,
  ): Observable<NutritionRecommendation[]> {
    const params = new HttpParams({ fromObject: { withCups, countryCode: country.toLowerCase() } });

    // Create http headers
    const headers = withAnalytics
      ? new HttpHeaders({
          product_id: productIds.join(';'),
          ean: productsEan.join(';'),
          main_item: productsMainItemId.join(';'),
        })
      : new HttpHeaders();

    return this.http.post(`${this.pathUrl.digitalRationing}/${program}`, body, { params, headers }).pipe(
      map((data: NutritionRecommendation | GestationNutritionRecommendation) =>
        this.unifyResponse(data).map(formatNutritionRecommendation),
      ),
      catchError(this.handleError.bind(this)),
    );
  }

  unifyResponse(response: NutritionRecommendation | GestationNutritionRecommendation): NutritionRecommendation[] {
    if (Object.keys(response).every((key) => !isNaN(Number(key)))) {
      return Object.values(response);
    }
    return [response as NutritionRecommendation];
  }

  getIBW(currentWeight: number, bcs: number): Observable<number> {
    let params = new HttpParams();
    params = params.set('bodyWeight', JSON.stringify(currentWeight));
    params = params.set('bcs', JSON.stringify(bcs));

    return this.http.get<{ idealBodyWeight: number }>(this.pathUrl.ibw, { params }).pipe(
      map((res) => res.idealBodyWeight),
      catchError(this.handleError.bind(this)),
    );
  }

  checkIBWValidity(bcs: number, idealBodyWeight: number, bodyWeight: number): Observable<boolean> {
    let params = new HttpParams();
    params = params.set('bcs', JSON.stringify(bcs));
    params = params.set('idealBodyWeight', JSON.stringify(idealBodyWeight));
    params = params.set('bodyWeight', JSON.stringify(bodyWeight));

    return this.http.get<{ idealBodyWeightValidity: boolean }>(this.pathUrl.ibwCheck, { params }).pipe(
      map((res) => res.idealBodyWeightValidity),
      catchError(this.handleError.bind(this)),
    );
  }

  private convertPoundsToKilograms(pounds: number): number {
    const kilograms = pounds * 0.453592;
    return kilograms;
  }

  public checkTBWValidity(
    speciesKey: LifestageType,
    targetBodyWeight: number,
    currentSystemPreferenceCode: SystemPreferenceCode,
    breedCode: string,
  ): boolean {
    const targetBodyWeightKg =
      currentSystemPreferenceCode === SystemPreferenceCode.imperialSystemCode
        ? this.convertPoundsToKilograms(targetBodyWeight)
        : targetBodyWeight;

    const bodyWeightBounds: Record<LifestageType, { minKg: number; maxKg: number }> = {
      [LifestageType.Puppy]: { minKg: 1, maxKg: 100 },
      [LifestageType.Kitten]: breedCode === Constants.maineCoonBreedCode ? { minKg: 4, maxKg: 10 } : { minKg: 2, maxKg: 7 },
    } as Record<LifestageType, { minKg: number; maxKg: number }>;

    const tbwValidity =
      targetBodyWeightKg >= bodyWeightBounds[speciesKey]?.minKg && targetBodyWeightKg <= bodyWeightBounds[speciesKey]?.maxKg;
    return tbwValidity;
  }

  /**
   * [POST]
   */
  planNextVisit(
    petInfo: PetInfo,
    firstDate: Date,
    program: Programs,
    currentBigMeasurementUnit: MeasurementCodeType,
    currentSystemPreferenceCode: SystemPreferenceCode,
    nextDate?: Date,
  ): Observable<NextVisitData> {
    const startDate = firstDate ?? new Date();
    let params = new HttpParams()
      .set('startDate', new Date(startDate).toISOString())
      .set('currentDate', new Date().toISOString())
      .set('program', program);

    if (nextDate) params = params.set('nextDate', new Date(nextDate).toISOString());

    const body = formatPetInfoToNextVisit(petInfo, currentBigMeasurementUnit);

    return this.http.post<NextVisitFromBack[]>(this.pathUrl.planNextVisit, body, { params }).pipe(
      map((response) =>
        MeasureHelper.formattedExpectedWeightFromBackToFront(response, currentBigMeasurementUnit, currentSystemPreferenceCode),
      ),
      catchError(this.handleError.bind(this)),
    );
  }

  recommendationPrograms(body: RecommendationProgramApiBody): Observable<FollowUpSelector> {
    return this.http.post<FollowUpSelector>(this.pathUrl.recommendationPrograms, body).pipe(catchError(this.handleError.bind(this)));
  }

  getSmartRecoProducts(
    body: SmartRecoApiBody,
    clinic: Clinic,
    market: Market,
    systemPreference: SystemPreferenceCode,
    limit?: number,
  ): Observable<{ coverage: Coverage; product: Product }[]> {
    const basicParameters = {
      locale: Helper.catalogLang(this.localeId, market),
      genderCode: body?.pet.genderCode,
    };
    if (limit) {
      basicParameters['limit'] = limit;
    }
    if (environment.isStandalone) {
      // Use payload and srsParameters for v2-new product apis
      const payload = ServicesHelper.mapSmartRecoApiBodyToSRSApiBody(body);
      const srsParameters = {
        locale: Helper.catalogLang(this.localeId, market).replace('-', '_'),
        genderCode: body?.pet.genderCode,
        countryCode: clinic?.companyAddress?.country.toLowerCase(),
      };
      if (limit) {
        srsParameters['limit'] = limit;
      }
      //remove this when v2-new will be used
      const smartRecoParameters = {
        ...basicParameters,
        countryCode: clinic?.companyAddress?.country.toLowerCase(),
        lifestage: body?.pet.lifestage,
      };
      const params: HttpParams = this.params(smartRecoParameters);
      return this.http.post<any>(this.pathUrl.smartRecoProductsSrs(), body, { params }).pipe(
        map((dto: { results: { coverage: Coverage; product: ProductV2 }[] }) => dto.results),
        map((recommendations) => {
          return recommendations
            .filter(({ product }) => this.productMapperService.isProductV2Complete(product))
            .map((reco) => {
              return {
                ...reco,
                coverage: reco.coverage,
                product: this.productMapperService.mapProductV2ToProduct(reco.product, systemPreference),
              };
            })
            .filter((reco) => reco.product?.packages?.length > 0);
        }),
      );
    } else {
      const smartRecoParameters = {
        ...basicParameters,
        countryCode: clinic?.companyAddress?.country.toLowerCase(),
        lifestage: body?.pet.lifestage,
      };
      const params: HttpParams = this.params(smartRecoParameters);
      return this.http.post<any>(this.pathUrl.smartRecoProducts(clinic?.id), body, { params }).pipe(
        map((dto: { results: { coverage: Coverage; product: ProductV2 }[] }) => dto.results),
        map((recommendations) => {
          return recommendations
            .filter(({ product }) => this.productMapperService.isProductV2Complete(product))
            .map((reco) => {
              return {
                ...reco,
                coverage: reco.coverage,
                product: this.productMapperService.mapProductV2ToProduct(reco.product, systemPreference),
              };
            })
            .filter((reco) => reco.product?.packages?.length > 0);
        }),
      );
    }
  }

  /**
   * [GET]
   */
  getSmartRecoNutritionalAssessment(body: SmartRecoApiBody, clinic: Clinic, market: Market, limit?: number): Observable<any> {
    const parameters = {
      locale: Helper.catalogLang(this.localeId, market),
      countryCode: clinic?.companyAddress?.country.toLowerCase(),
    };
    const params = this.params(parameters);
    const path = this.pathUrl.nutritionalAssessment();
    return this.http.post<any>(path, body, { params }).pipe(
      tap((assessment) => {
        if (!assessment || assessment.macroNutrients?.length < 1 || assessment.otherNutrients?.length < 1) {
          catchError(this.handleError.bind(this));
        }
      }),
      map((data) => formatNutritionalAssessment(data)),
      catchError(this.handleError.bind(this)),
    );
  }

  /**
   * [GET]
   */
  getSmartRecoNotRecommendedProduct(
    id: string,
    body: SmartRecoApiBody,
    clinic: Clinic,
    market: Market,
  ): Observable<ProductRecommendationV2> {
    const parameters = {
      locale: Helper.catalogLang(this.localeId, market),
      countryCode: clinic?.companyAddress?.country.toLowerCase(),
    };

    const params: HttpParams = this.params(parameters);
    if (environment.isStandalone) {
      const srsParameters = {
        locale: Helper.catalogLang(this.localeId, market).replace('-', '_'),
        countryCode: clinic?.companyAddress?.country.toLowerCase(),
      };
      // Use payload and srsParams for v2-new product apis
      const srsParams: HttpParams = this.params(srsParameters);
      const payload = ServicesHelper.mapSmartRecoApiBodyToSRSApiBody(body);
      return this.http
        .post<ProductRecommendationBack>(this.pathUrl.srsSmartRecoNotrecommendedProduct(id), body, { params })
        .pipe(catchError(this.handleError.bind(this)));
    } else {
      return this.http
        .post<ProductRecommendationBack>(this.pathUrl.smartRecoNotRecommendedProduct(clinic.id, id), body, { params })
        .pipe(catchError(this.handleError.bind(this)));
    }
  }

  getBreedPetProfile = ({
    breedCode,
    dateOfBirth,
    genderCode,
    lifestagesPlanDuration,
  }: BreedPetProfileParameters): Observable<BreedPetProfileModel> => {
    return this.http.get<BreedPetProfileModel>(this.pathUrl.petProfile(breedCode), {
      params: {
        ...(dateOfBirth && {
          dateOfBirth: formatDate(dateOfBirth, 'yyyy-MM-dd', this.localeId),
        }),
        ...(genderCode && { genderCode }),
        lifestagesPlanDuration: lifestagesPlanDuration?.toString() || defaultLifestagesPlanDuration,
      },
    });
  };
}
